import React, { useState } from 'react';
import './RestaurantMap.css';

interface RestaurantMapProps {
  address?: string;
  height?: string;
  compact?: boolean;
}

const RestaurantMap: React.FC<RestaurantMapProps> = ({ 
  address = "263 Weller Ave #3, Kingston, ON K7K 5B5",
  height = "200px",
  compact = false
}) => {
  const [userLocation, setUserLocation] = useState<{lat: number, lng: number} | null>(null);
  const [locationError, setLocationError] = useState<string>('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const getUserLocation = () => {
    if (!('geolocation' in navigator)) {
      setLocationError('Geolocation not supported');
      return null;
    }

    setIsGettingLocation(true);
    setLocationError('');

    return new Promise<{lat: number, lng: number} | null>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(location);
          setIsGettingLocation(false);
          resolve(location);
        },
        (error) => {
          setLocationError('Location access denied');
          setIsGettingLocation(false);
          resolve(null);
        }
      );
    });
  };

  const encodedAddress = encodeURIComponent(address);
  const mapUrl = `https://maps.google.com/maps?q=${encodedAddress}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
  
  const getDirectionsUrl = async () => {
    if (userLocation) {
      const origin = `${userLocation.lat},${userLocation.lng}`;
      return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${encodedAddress}`;
    }
    
    // Get location on-demand when directions are requested
    const location = await getUserLocation();
    if (location) {
      const origin = `${location.lat},${location.lng}`;
      return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${encodedAddress}`;
    }
    
    return `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
  };

  const handleDirectionsClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    const directionsUrl = await getDirectionsUrl();
    window.open(directionsUrl, '_blank', 'noopener,noreferrer');
  };

  if (compact) {
    return (
      <div className="restaurant-map-compact">
        <div className="map-wrapper-compact" style={{ height }}>
          <iframe
            src={mapUrl}
            width="100%"
            height="100%"
            style={{ border: 0, borderRadius: '8px' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Restaurant Location Map"
            className="map-iframe"
          />
        </div>
        <div className="map-actions-compact">
          <button
            onClick={handleDirectionsClick}
            className="map-btn-compact"
            disabled={isGettingLocation}
          >
            {isGettingLocation ? 'Getting Location...' : 'Get Directions'}
          </button>
          {locationError && (
            <span className="location-error">
              Location unavailable
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="restaurant-map-container">
      <div className="map-header">
        <h3>Find Us</h3>
        <p>{address}</p>
      </div>
      <div className="map-wrapper" style={{ height }}>
        <iframe
          src={mapUrl}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Restaurant Location Map"
          className="map-iframe"
        />
      </div>
      <div className="map-actions">
        <button
          onClick={handleDirectionsClick}
          className="map-btn primary"
          disabled={isGettingLocation}
        >
          {isGettingLocation ? 'Getting Location...' : 'Get Directions'}
        </button>
        {locationError && (
          <span className="location-error">
            Location unavailable
          </span>
        )}
      </div>
    </div>
  );
};

export default RestaurantMap;
